import { ErroHttp } from '@/shared/utils/erroHttp';
import type { RecusaDoUsoDocumentoPadrao } from '../types/documentosPadraoTypes';

const MENSAGEM_GENERICA = 'Não foi possível usar o documento no processo. Tente novamente.';

/**
 * O `error.code` do core decide a frase: a mensagem que ele manda é técnica (traz ids e versões) e
 * não é para a pessoa ler no painel. Estes são os códigos da BIBLIOTECA, iguais onde quer que o
 * documento seja usado; os do destino (autos ou rascunho) vêm de quem chama.
 */
const MENSAGEM_DA_BIBLIOTECA: Record<string, string> = {
  DOCUMENTO_PADRAO_DESATUALIZADO:
    'Este documento ganhou uma versão nova. A lista foi atualizada; selecione-o de novo.',
  DOCUMENTO_PADRAO_ARQUIVADO: 'Este documento foi arquivado na biblioteca e não pode ser usado.',
  DOCUMENTO_PADRAO_VENCIDO:
    'A validade deste documento venceu. Atualize-o na biblioteca antes de usar.',
  DOCUMENTO_PADRAO_PRODUTO_INCOMPATIVEL: 'Este documento não está liberado para Arbitragem.',
};

const MENSAGEM_POR_STATUS: Record<number, string> = {
  403: 'Você não tem permissão para usar documentos padrão neste processo.',
  404: 'Este documento não está mais disponível para você. A lista foi atualizada.',
  429: 'Muitas tentativas seguidas. Aguarde um instante e tente novamente.',
  503: 'O processo está sendo atualizado. Tente novamente em instantes.',
};

/** O documento mudou desde a lista: o painel recarrega do início em vez de insistir na seleção. */
const CODIGOS_DE_LISTA_DESATUALIZADA = new Set([
  'DOCUMENTO_PADRAO_DESATUALIZADO',
  'DOCUMENTO_PADRAO_ARQUIVADO',
  'DOCUMENTO_PADRAO_VENCIDO',
]);

/**
 * Traduz a falha do `POST .../documentos-padrao` (autos ou rascunho) para o contrato do painel da
 * biblioteca. `mensagensDoDestino` traz as frases dos códigos que só o destino conhece (processo
 * encerrado, petição já anexada) e a da mesma versão repetida, que diz ONDE ela já está. Falha sem
 * resposta (rede) cai na mensagem genérica, com `status` nulo: é a única em que repetir a mesma
 * confirmação faz sentido.
 *
 * @example recusaDoUsoDocumentoPadrao(erro, { PROCESSO_ENCERRADO: 'Este processo foi encerrado…' })
 */
export function recusaDoUsoDocumentoPadrao(
  erro: unknown,
  mensagensDoDestino: Record<string, string>,
): RecusaDoUsoDocumentoPadrao {
  if (!(erro instanceof ErroHttp)) {
    return { mensagem: MENSAGEM_GENERICA, status: null, codigo: null, listaDesatualizada: false };
  }
  const porCodigo = erro.codigo
    ? (mensagensDoDestino[erro.codigo] ?? MENSAGEM_DA_BIBLIOTECA[erro.codigo])
    : undefined;
  return {
    mensagem: porCodigo ?? MENSAGEM_POR_STATUS[erro.status] ?? MENSAGEM_GENERICA,
    status: erro.status,
    codigo: erro.codigo,
    listaDesatualizada:
      erro.status === 404 ||
      (erro.codigo !== null && CODIGOS_DE_LISTA_DESATUALIZADA.has(erro.codigo)),
  };
}
